import { useImmutableChannel } from '@/api/hooks'
import { addGifsToChannel } from '@/app/util/channel-api'
import { giphyLocalUrl } from '@/app/util/env'
import { checkIfPlaceholder } from '@/app/util/gif-skeleton'
import { FlashMessageContext } from '@/context/flash-message'
import { getCookie, getPost } from '@/util/url'
import { IChannel, IGif } from '@giphy/js-types'
import { ReactNode, useContext } from 'react'
import { timeoutFetch } from 'utils/src/api/fetch'
import { ModalContext } from '../../modal'
import { useChannelMutate } from '../hooks'
import { Breadcrumbs } from '../ui'

export const CreateCollectionForm = ({
    className,
    children,
    featuredGif,
    enabled,
    onCreated,
}: {
    enabled: boolean
    className: string
    children: ReactNode
    featuredGif?: IGif
    /**
     * Where to go once the collection exists. Without it the form lands on the
     * `editCollections` modal, which is right for the channel pages but wrong
     * for the creation tools' picker — there the user is mid-upload and wants
     * the new collection selected and the picker back. Legacy does the same via
     * `collectionCreatedSuccessMessage` (shared/components/creation/info-fields/
     * fields/channels.tsx:126-130).
     */
    onCreated?: (channel: IChannel) => void
}) => {
    const csrftoken = getCookie('csrftoken')
    const { showMessage } = useContext(FlashMessageContext)
    const mutateChannel = useChannelMutate()
    const { goBack, setModalType, modal } = useContext(ModalContext)
    const { data: channel } = useImmutableChannel(modal?.channelId)
    return (
        <form
            autoComplete="off"
            className={className}
            action={async (formData: FormData) => {
                if (!enabled) {
                    return
                }
                const result = await timeoutFetch({
                    url: `${giphyLocalUrl}/api/v4/channels`,
                    options: {
                        ...getPost(csrftoken),
                        body: JSON.stringify(Object.fromEntries(formData)),
                    },
                })
                if (result.ok) {
                    const newChannel = (await result.json()) as IChannel
                    if (featuredGif && !checkIfPlaceholder(featuredGif)) {
                        const addGifResult = await addGifsToChannel([featuredGif.id], newChannel.id)
                        if (addGifResult.ok) {
                            showMessage({
                                message: 'Created collection',
                                type: 'success',
                            })
                        }
                    } else {
                        showMessage({
                            message: 'Created collection',
                            type: 'success',
                        })
                    }
                    await mutateChannel({ ...newChannel, featured_gif: featuredGif! })

                    if (onCreated) {
                        onCreated(newChannel)
                    } else if (channel) {
                        setModalType({
                            name: 'editCollections',
                            channelId: channel.id,
                            title: <Breadcrumbs channel={channel} />,
                        })
                    } else {
                        goBack()
                    }
                } else {
                    showMessage({
                        message: 'Failed to create to collection',
                        type: 'error',
                    })
                }
            }}
        >
            {children}
        </form>
    )
}
